/**
 * HyperDX Error Boundary
 * React error boundary that captures and reports errors to HyperDX
 *
 * Features:
 * - Captures React component errors
 * - Reports to HyperDX with component stack
 * - Shows user-friendly fallback UI
 * - Allows users to recover by reloading
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { trackHyperDXError, trackHyperDXAction } from '../../services/observability';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class HyperDXErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console for development
    console.error('[HyperDXErrorBoundary] Caught error:', error, errorInfo);

    // Store error info in state
    this.setState({ errorInfo });

    // Report to HyperDX
    trackHyperDXError(error, {
      component_stack: errorInfo.componentStack?.substring(0, 1000) || 'unknown',
      error_boundary: 'HyperDXErrorBoundary',
    });

    // Also track as an action for easier querying
    trackHyperDXAction('react.error_boundary_triggered', {
      error_name: error.name,
      error_message: error.message.substring(0, 200),
    });
  }

  handleReload = (): void => {
    trackHyperDXAction('error_boundary.reload_clicked');
    window.location.reload();
  };

  handleGoHome = (): void => {
    trackHyperDXAction('error_boundary.go_home_clicked');
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>

            <p className="text-gray-600 mb-6">
              We're sorry, but something unexpected happened. Our team has been
              notified and is working to fix the issue.
            </p>

            {process.env.NODE_ENV === 'development' && error && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg text-left">
                <p className="text-sm font-mono text-red-800 break-words">
                  {error.name}: {error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition"
              >
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default HyperDXErrorBoundary;
